/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { first, map } from "rxjs/operators";
import { AbstractObjectList, uuid } from "@src/app/model/abstract-object";
import { User } from "@src/app/model/user";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { BatchType, ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { POBValidators, validateDatePOB } from "@src/app/modules/importer/General/POBValidators";
import { BCodeService } from "@src/app/modules/budget/bcode.service";
import { BCode } from "@src/app/model/bcode";
import { ParseOptionBaseBCode } from "@src/app/modules/importer/General/ParseOptionBaseBCode";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { CycleService } from "@src/app/modules/budget/cycle.service";
import { BankAccountService } from "@src/app/modules/txn/bank-account.service";
import { BankAccount } from "@src/app/model/bankAccount";
import { NrHelper } from "@src/app/shared/NrHelper";
import { TenantService } from '@src/app/shared/tenant.service';
import { MyInjector } from "@src/app/app.module";
import { ImportField } from "@src/app/modules/importer/ImportField";
import { Period } from "@src/app/model/period";
import { PeriodService } from "@src/app/modules/budget/period.service";
import { ScheduleService } from "@src/app/modules/budget/schedule.service";
import { Schedule } from "@src/app/model/schedule";
import { ParseOptionBaseMapped } from "@src/app/modules/importer/General/ParseOptionBaseMapped";

interface ImportData {
    reference: string, txnDate: string, note: string, details: ImportDataItem[], totalDebit: number, totalCredit: number
}
interface ImportDataItem { bCodeId: uuid, scheduleId: uuid, bankAccountId: uuid, debit: number, credit: number, reference?: string, txnDate?: string, note: string }

@Injectable({
    providedIn: 'root'
})
export class JournalEntryParser implements ImportParser {

    gps = new GeneralParser();
    bCodeSvc = inject(BCodeService);
    currentUserSvc = inject(CurrentUserService);
    bankAccountSvc = inject(BankAccountService);
    http = inject(HttpClient);
    dialog = inject(MatDialog);
    cycleSvc = inject(CycleService);
    periodSvc = inject(PeriodService);
    scheduleSvc = inject(ScheduleService);

    batchType: BatchType = 'Journal';

    periods : Period[] = [];
    bCodes : BCode[] = [];
    schedules : Schedule[] = [];
    bankAccounts: BankAccount[] = [];
    importRows: ImportDataItem[] = [];

    importDoc: ImportDoc;
    currentUser: User;

    referenceValidator = {
        validateRow: (field: ImportField) => {
            const val = field.getValue();
            if (!val || String(val).length > 128) {
                return $localize `Reference is required, cannot be more than 128 characters (prefer < 30)`;
            }
            return null;
        }
    } as ParseValidator;    

    bCodePOB = new ParseOptionBaseBCode('Budget Code', 'bCodeId', [POBValidators.requiredPOB()]);
    schedulePOB = new ParseOptionBaseMapped('Schedule', 'scheduleId').allowNull();
    debitPOB = new ParseOptionBase('Debit', 'debit').asNumeric();
    creditPOB = new ParseOptionBase('Credit', 'credit').asNumeric();
    referencePOB = new ParseOptionBase('Reference', 'reference', [POBValidators.requiredPOB(), this.referenceValidator]);
    txnDatePOB = new ParseOptionBase('Transaction Date', 'txnDate')
        .addValidator(validateDatePOB())
        .addValidator({validateRow: this.validatePeriodOpen.bind(this)});
    notePOB = new ParseOptionBase('Note', 'note');

    expectedDebtors = 0;
    expectedCreditors = 0;

    availableImportItems = [
        this.referencePOB,
        this.txnDatePOB,        
        this.bCodePOB,
        this.schedulePOB,
        this.debitPOB,
        this.creditPOB,
        this.notePOB,
    ];


    setUp() {
        this.importDoc = new ImportDoc();

        return forkJoin({
            currentUser: this.currentUserSvc.getCurrentUser().pipe(first()),
            bCodes: this.bCodeSvc.get<BCode>(true),
            bankAccounts: this.bankAccountSvc.get<BankAccount>(true),
            periods: this.periodSvc.get<Period>(true),
            schedules: this.scheduleSvc.get<Schedule>(true),
            dbcs: this.currentUserSvc.getDefaultBCodes().pipe(first())
        }).pipe(map(result => {
            console.log(result);
            this.currentUser = result.currentUser;
            this.bCodes = result.bCodes;
            this.bankAccounts = result.bankAccounts;
            this.periods = result.periods;
            this.bCodePOB.setOptions(result.bCodes, this.importDoc);
            this.schedulePOB.setOptions(result.schedules, this.importDoc);

            return true;
        }));
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    validatePeriodOpen(field: ImportField) {

        const val = field.getValue();
        const prd = this.periods.find(p => p.to >= val && p.from <= val);
        if (!prd) {
            return $localize` There is no period for date ${val}`
        } if (prd.statusId !== Period.STATUS.OPEN.id) {
            return $localize` Period ${prd.name} is not open for transactions`;
        }
        return null;
    }


    validateData() {

        const rows = this.importDoc.getGeneralRows();
        const journals = new Map<string, ImportData>();

        rows.forEach( row => {
            const item = row.importItem as ImportDataItem;
            if (!journals.has(item.reference)) {
                journals.set(item.reference, {
                    reference: item.reference, 
                    txnDate: item.txnDate, 
                    note: (item.note || '') + '\n', 
                    details: [],
                    totalDebit: 0,
                    totalCredit: 0
                })
            }
            const j = journals.get(item.reference);
            if (j.txnDate !== item.txnDate) {
                row.addError('All dates for transaction reference must match');
            }
            
            const dtl = {
                bCodeId: item.bCodeId, 
                scheduleId: item?.scheduleId, 
                debit: item.debit, 
                credit: item.credit, 
                note: item.note
            } as ImportDataItem
            const ba = this.bankAccounts.find( ba => ba.bCodeId === item.bCodeId);
            if (ba) dtl.bankAccountId = ba.id;
            j.details.push(dtl);
            j.totalDebit += item.debit;
            j.totalCredit += item.credit;
            if (!j.note.includes(item.note)) {
                j.note += ((item.note || '') + '\n');
            }
        })
        for (const j of journals.values()) {
            if (!NrHelper.equal(j.totalDebit, j.totalCredit)) {
                this.importDoc.addNote(
                    $localize `Journal ${j.reference} is not balanced debits: ${j.totalDebit} - credits : ${j.totalCredit}`,
                    null, true
                )
            }
        }
        console.log(journals);

        this.importDoc.finalise();
        const journalArray = Array.from(journals.values());
        journalArray.sort((a, b) => a.reference.localeCompare(b.reference));
        return journalArray;
    }


    postToServer(): Observable<AbstractObjectList> {
        const postItems = this.gps.getItemsToPost();
        const postData = {
            journals: this.validateData(),
        }
        console.log({postItems, postData});
        if (this.importDoc.hasErrors()) {
            return of(null);
        } else {
            const url = TenantService.ajaxPath + 'importFile/' + this.batchType;
            const http = MyInjector.instance.get(HttpClient);
            return http.post<AbstractObjectList>(url, postData);
        }
    }    
}
